import {
    createMFHEstate,
    createMfhProjectPlanSnapshot,
    deleteMFHBuilding,
    deleteMfhDDQuestion,
    deleteMfhProjectPlanSnapshot,
    fetchAllMfhBuildings,
    fetchAssetsInPipelineList,
    fetchEligibleAssetsList,
    fetchMfhBuildings,
    fetchMfhHierarchy,
    fetchMfhList,
    fetchMfhPipelineFilteredConfig,
    fetchMfhRentRoll,
    fetchMfhSourcingList,
    fetchOneMfh,
    getAssetRentRoll,
    getEstarCoreMFHIds,
    getMFHEstarCoreEstates,
    getMfhDDQuestions,
    getMfhProjectPlanSnapshots,
    getMfhStats,
    moveEstatesToBuildingLocation,
    updateMFH,
    upsertMFHBuilding,
    upsertMFHBuildingLocation,
    upsertMfhDDQuestion,
} from '@/api/mfh';
import { grantMfhPortalView } from '@/api/basecamp.api';
import { getMfhLatestRents } from '@/api/property-management.api';
import { useRouteParams } from '@/hooks';
import { withLoading, MessageType, showMessage } from '@/shared';
import { useStore } from 'vuex';
import { isAxiosError } from 'axios';
import {
    ApartmentType,
    CondoSalesCondition,
    type EnergyConsumptionCategory,
    FloorLocation,
    type FormSchema,
    type GanttChartData,
    type IBaseDDQuestion,
    type ICreateMfhEstateRequest,
    type ILocationDTO,
    type IMFH,
    type IMFHProjectPlan,
    type IMfhBuildingQuery,
    type IMfhBuildingRequestParams,
    type IMfhFilter,
    type IPaginationFields,
    type IPipelineMFH,
    type InvestmentRiskProfile,
    type MFHBuildingExpandOpts,
    type MFHEstateManagementState,
    type MFHExpandOpts,
    type MFHProjectPlanExpandOpts,
    type MFHSourcingState,
    type MFHSubsidyFinancingState,
    MfhInPipelineSourcingStates,
    RentApartmentCondition,
    rentRollStatusColorMap,
    RentalContractRentIncreaseType,
    apartmentConditionToRentRoll,
    apartmentTypeToRentRoll,
    condoSalesConditionToRentRoll,
    floorLocationToRentRoll,
    rentIncreaseTypeToRentRoll,
    MandateType,
} from '@condo/domain';
import type { GetMfhLatestRentsQueryResponse } from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { omit } from 'lodash-es';
import { type MaybeRef, computed, ref, unref } from 'vue';

export const useMfhs = (queryOpts: MaybeRef<IMfhFilter>, expandOptions?: MFHExpandOpts, enabled: MaybeRef<boolean> = true) => {
    return useQuery(
        ['mfh-list', queryOpts],
        async (): Promise<{ mfhs: IMFH[]; total: number }> =>
            withLoading(async () => {
                return fetchMfhList(unref(queryOpts), expandOptions);
            }),
        {
            initialData: { mfhs: [], total: 0 },
            enabled,
        },
    );
};

export const useMfhFilter = (initialOverride?: Record<any, any>) => {
    const { getRouteParam, getRouteParamArray } = useRouteParams();

    const defaultFilter = {
        page: 1,
        size: 50,
        region: [],
        order: 'DESC',
        unScreened: false,
        onlyComparable: false,
        sourcingState: Object.values(MfhInPipelineSourcingStates),
        orderBy: 'mfhId',
        street: '',
    } as IMfhFilter;

    const filter = ref<IMfhFilter>({
        page: getRouteParam('page', Number) ?? defaultFilter.page,
        size: getRouteParam('size', Number) ?? defaultFilter.size,
        region: getRouteParamArray('region', String) ?? defaultFilter.region,
        order: (getRouteParam('order', String) as 'ASC' | 'DESC') ?? defaultFilter.order,
        orderBy: getRouteParam('orderBy', String) ?? defaultFilter.orderBy,
        street: getRouteParam('street', String),
        city: getRouteParamArray('city', String),
        sourcingState: getRouteParamArray('sourcingState', String, defaultFilter.sourcingState) as MFHSourcingState[],
        subsidyFinancingState: getRouteParamArray('subsidyFinancingState', String) as MFHSubsidyFinancingState[],
        estateManagementState: getRouteParamArray('estateManagementState', String) as MFHEstateManagementState[],
        unScreened: getRouteParam('unScreened', String) === 'true',
        onlyComparable: getRouteParam('onlyComparable', String) === 'true',
        constructionYearFrom: getRouteParam('constructionYearFrom', Number),
        constructionYearTo: getRouteParam('constructionYearTo', Number),
        purchasingPriceFrom: getRouteParam('purchasingPriceFrom', Number),
        purchasingPriceTo: getRouteParam('purchasingPriceTo', Number),
        rentableAreaFrom: getRouteParam('rentableAreaFrom', Number),
        rentableAreaTo: getRouteParam('rentableAreaTo', Number),
        energyEfficiencyCategory: getRouteParamArray('energyEfficiencyCategory', String) as EnergyConsumptionCategory[],
        adPublishingDateFrom: getRouteParam('adPublishingDateFrom', String),
        adPublishingDateTo: getRouteParam('adPublishingDateTo', String),
        assetPartnerId: getRouteParamArray('assetPartnerId', Number),
        candidateAssetPartnerId: getRouteParamArray('candidateAssetPartnerId', Number),
        investmentRiskProfile: getRouteParamArray('investmentRiskProfile', String) as InvestmentRiskProfile[],
        mandateType: getRouteParamArray('mandateType', String) as MandateType[],
        ...(initialOverride ?? {}),
    });

    const setFilter = async (updatedFilter?: Partial<IMfhFilter>) => {
        filter.value = {
            ...defaultFilter,
            ...initialOverride,
            ...updatedFilter,
        };
    };

    return { filter, setFilter };
};

export const useMfh = (mfhId: MaybeRef<number | undefined>, expandOpts: MaybeRef<MFHExpandOpts>) => {
    const queryKey = ['mfh', mfhId, expandOpts];

    const query = useQuery(
        queryKey,
        () =>
            withLoading(() => {
                const opts = {
                    mfhId: unref(mfhId),
                    expandOpts: unref(expandOpts),
                };
                return fetchOneMfh(opts);
            }),
        {
            initialData: null,
            enabled: computed(() => !!unref(mfhId)),
        },
    );

    return { ...query };
};

export const useMfhMutations = (mfhId: number) => {
    const queryKey = ['mfh', mfhId];
    const queryClient = useQueryClient();

    const { mutateAsync: updateMFHMutation } = useMutation((mfh: Partial<IMFH>) => updateMFH(mfhId, mfh), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
        },
    });

    return {
        update: updateMFHMutation,
    };
};

export const useMfhDDQuestions = <const TSchema extends Array<FormSchema> = Array<FormSchema>>(mfhId: number, type: TSchema) => {
    return useQuery(['mfh-dd-questions', mfhId, type], () => getMfhDDQuestions<TSchema>(mfhId, type), {
        initialData: [],
    });
};

export const useMfhDDQuestionsMutations = <T extends FormSchema = FormSchema>(mfhId: number) => {
    const { mutateAsync: upsertDDQuestion } = useMutation((question: IBaseDDQuestion<T>) => upsertMfhDDQuestion(question));
    const { mutateAsync: deleteDDQuestion } = useMutation((params: { mfhId: number; questionId: number }) => deleteMfhDDQuestion(mfhId, params.questionId));

    return {
        upsertDDQuestion,
        deleteDDQuestion,
    };
};

export const useAllMFHBuildings = (
    query: MaybeRef<IMfhBuildingQuery>,
    expand: MFHBuildingExpandOpts,
    pagination?: MaybeRef<IPaginationFields>,
    enabled?: MaybeRef<boolean>,
) => {
    const queryKey = ['all-mfh-buildings', query, expand, pagination];

    return useQuery(queryKey, () => fetchAllMfhBuildings(unref(query), expand, unref(pagination)), {
        initialData: [],
        enabled,
    });
};

export const useMFHBuildings = (mfhId: MaybeRef<number | undefined>, expand: MFHBuildingExpandOpts, queryOptions?: { refetchInterval?: number }) => {
    const queryKey = ['mfh-buildings', mfhId, expand];
    const enabled = computed(() => !!unref(mfhId));

    return useQuery(queryKey, () => fetchMfhBuildings(unref(mfhId)!, expand), {
        initialData: [],
        refetchInterval: queryOptions?.refetchInterval,
        enabled,
    });
};

export const useMFHRentRoll = (mfhId: number) => {
    return useQuery(['mfh-rent-roll', mfhId], () => fetchMfhRentRoll(mfhId), {
        initialData: [],
    });
};

export const useMFHRentRollGridOptions = (
    mfhId: number,
    mfhBuildingExpandOpts: MFHBuildingExpandOpts = {
        select: ['mfhBuildingId'],
        expand: {
            location: {
                select: ['locationId', 'street', 'houseNumber'],
            },
            estates: {
                select: ['estateId', 'mfhBuildingId'],
                expand: {
                    attributes: {
                        select: ['attrId', 'apartmentNumber', 'floor', 'floorLocation'],
                    },
                },
            },
        },
    },
) => {
    const {
        data: mfhBuildings,
        refetch: refetchMfhBuildings,
        isFetching: isFetchingBuildings,
    } = useMFHBuildings(mfhId, mfhBuildingExpandOpts, { refetchInterval: 5000 });

    const mfhBuildingOptions = computed(() =>
        mfhBuildings.value
            .filter(b => !!b.location?.street && !!b.location?.houseNumber)
            .map(b => {
                return {
                    label: `${b.location!.street} ${b.location!.houseNumber}`,
                    value: b.mfhBuildingId,
                };
            }),
    );

    const floorLocationOptions = Object.values(FloorLocation).map(value => {
        return {
            label: floorLocationToRentRoll[value],
            value,
        };
    });

    const apartmentTypeOptions = [
        ApartmentType.Apartment,
        ApartmentType.Office,
        ApartmentType.Shop,
        ApartmentType.Gastronomy,
        ApartmentType.CommercialOther,
        ApartmentType.Parking,
    ].map(value => {
        return {
            label: apartmentTypeToRentRoll[value],
            value,
        };
    });

    const apartmentConditionOptions = Object.values(RentApartmentCondition).map(value => {
        return {
            label: apartmentConditionToRentRoll[value],
            value,
        };
    });

    const condoSalesConditionOptions = Object.values(CondoSalesCondition).map(value => {
        return {
            label: condoSalesConditionToRentRoll[value],
            value,
        };
    });

    const rentIncreaseTypeOptions = [RentalContractRentIncreaseType.FRI, RentalContractRentIncreaseType.IRI, RentalContractRentIncreaseType.NO_INCREASE].map(
        value => {
            return {
                label: rentIncreaseTypeToRentRoll[value],
                value,
            };
        },
    );

    const salutationOptions = ['Frau', 'Herr', 'Divers'].map(value => {
        return {
            label: value,
            value,
        };
    });

    return {
        rentRollStatusColorMap,
        floorLocationOptions,
        apartmentTypeOptions,
        apartmentConditionOptions,
        condoSalesConditionOptions,
        rentIncreaseTypeOptions,
        mfhBuildings,
        isFetchingBuildings,
        refetchMfhBuildings,
        mfhBuildingOptions,
        salutationOptions,
    };
};

export const useMFHBuildingsMutations = (mfhId: number) => {
    const queryKey = ['mfh-buildings', mfhId];
    const queryKeyForMfhHierarchy = ['mfh-hierarchy', mfhId];
    const queryClient = useQueryClient();

    const { mutateAsync: upsertMFHLocationMutation } = useMutation(
        (params: { mfhBuildingId: number; location: ILocationDTO }) => upsertMFHBuildingLocation(params.mfhBuildingId, params.location),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKey);
                queryClient.invalidateQueries(queryKeyForMfhHierarchy);
            },
        },
    );

    const { mutateAsync: upsertMFHBuildingMutation } = useMutation((params: IMfhBuildingRequestParams) => upsertMFHBuilding(mfhId, params), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries(queryKeyForMfhHierarchy);
        },
    });

    const { mutateAsync: deleteBuildingMutation } = useMutation((params: { mfhBuildingId: number }) => deleteMFHBuilding(mfhId, params.mfhBuildingId), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries(queryKeyForMfhHierarchy);
        },
    });

    const { mutateAsync: moveEstatesToBuildingLocationMutation } = useMutation(
        (params: { estateIds: number[]; mfhBuildingId: number }) =>
            moveEstatesToBuildingLocation({
                estateIds: params.estateIds,
                mfhBuildingId: params.mfhBuildingId,
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKey);
                queryClient.invalidateQueries(queryKeyForMfhHierarchy);
                queryClient.invalidateQueries(['mfh-building']);
            },
        },
    );

    const { mutateAsync: addEstateToBuildingMutation } = useMutation((value: ICreateMfhEstateRequest) => createMFHEstate(value), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKeyForMfhHierarchy);
        },
    });

    return {
        upsertLocation: upsertMFHLocationMutation,
        upsertBuilding: upsertMFHBuildingMutation,
        deleteBuilding: deleteBuildingMutation,
        moveEstatesToBuildingLocation: moveEstatesToBuildingLocationMutation,
        addEstateToBuildingMutation: addEstateToBuildingMutation,
    };
};

export const useMfhsSourcingList = (queryOpts?: MaybeRef<IMfhFilter>, enabled = true) => {
    const query = useQuery(
        ['mfh-sourcing-list', queryOpts],
        async (): Promise<{ mfhs: IMFH[]; total: number }> =>
            withLoading(async () => {
                return fetchMfhSourcingList(unref(queryOpts));
            }),
        {
            initialData: { mfhs: [], total: 0 },
            enabled,
        },
    );

    const { mutateAsync: updateMFHMutation } = useMutation((mfh: Partial<IMFH>) => updateMFH(mfh.mfhId!, omit(mfh, ['mfhId'])), {
        onSuccess: async () => {
            await query.refetch();
        },
    });

    return { ...query, update: updateMFHMutation };
};

export const useEligibleAssetsList = (queryOpts?: MaybeRef<IMfhFilter>, enabled = true) => {
    return useQuery(
        ['eligible-assets-list', queryOpts],
        async (): Promise<{ mfhs: IMFH[]; total: number }> => withLoading(async () => fetchEligibleAssetsList(unref(queryOpts))),
        {
            initialData: { mfhs: [], total: 0 },
            enabled,
        },
    );
};

export const useAssetsInPipelineList = (queryOpts?: MaybeRef<IMfhFilter>, includeCandidateAssetPartners?: boolean, enabled = true) => {
    return useQuery(
        ['assets-in-pipeline-list', queryOpts, includeCandidateAssetPartners],
        async (): Promise<{ mfhs: IPipelineMFH[]; total: number }> =>
            withLoading(async () => fetchAssetsInPipelineList(unref(queryOpts), includeCandidateAssetPartners)),
        {
            initialData: { mfhs: [], total: 0 },
            enabled,
        },
    );
};

export const useMfhPipelineFilteredConfig = (
    filters: MaybeRef<{
        city?: string[];
        sourcingState?: MFHSourcingState[];
        assetPartnerId?: number[];
        candidateAssetPartnerId?: number[];
        mfhPortfolioId?: number[];
    }>,
) => {
    return useQuery(['mfh-pipeline-filtered-config', filters], () => fetchMfhPipelineFilteredConfig(unref(filters)), {
        initialData: { streets: [] },
    });
};

export const useMfhHierarchy = (mfhId: number) => {
    return useQuery(['mfh-hierarchy', mfhId], () => fetchMfhHierarchy(mfhId), {
        initialData: null,
    });
};

export const useMFHEstarCoreEstates = (mfhId: number) => {
    return useQuery(['mfh-estarcore-estates', mfhId], () => getMFHEstarCoreEstates(mfhId), {
        initialData: [],
        refetchInterval: 0,
        refetchOnWindowFocus: false,
    });
};

export const useEstarCoreMFHIds = () => {
    return useQuery(['estar-core-mfh-ids'], () => getEstarCoreMFHIds(), {
        initialData: [],
    });
};

export const useAssetRentRoll = (mfhId: number) => {
    return useQuery(['asset-rent-roll', mfhId], () => getAssetRentRoll(mfhId), {
        initialData: [],
    });
};

export const useMfhStats = (mfhId: number) => {
    return useQuery(['mfh-stats', mfhId], () => getMfhStats(unref(mfhId)));
};

export const useMfhLatestRents = (mfhId: MaybeRef<number>) => {
    return useQuery<GetMfhLatestRentsQueryResponse>(['mfh-latest-rents', mfhId], () => getMfhLatestRents(unref(mfhId)), {
        initialData: [],
        enabled: computed(() => !!unref(mfhId)),
    });
};

// Project Plan Snapshots
export const useMfhProjectPlanSnapshots = (mfhId: MaybeRef<number>, expandOpts?: MFHProjectPlanExpandOpts) => {
    return useQuery<IMFHProjectPlan[]>(
        ['mfh-project-plan-snapshots', mfhId, expandOpts],
        () => getMfhProjectPlanSnapshots(unref(mfhId), { expand: expandOpts }),
        {
            initialData: [],
            enabled: computed(() => !!unref(mfhId)),
        },
    );
};

export const useCreateMfhProjectPlanSnapshot = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mfhId, ganttData, description }: { mfhId: number; ganttData: GanttChartData; description?: string }) =>
            createMfhProjectPlanSnapshot(mfhId, ganttData, description),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries(['mfh-project-plan-snapshots', variables.mfhId]);
        },
    });
};

export const useDeleteMfhProjectPlanSnapshot = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mfhProjectPlanId, mfhId }: { mfhProjectPlanId: number; mfhId: number }) => deleteMfhProjectPlanSnapshot(mfhProjectPlanId),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries(['mfh-project-plan-snapshots', variables.mfhId]);
        },
    });
};

export const useViewInExternalPortal = () => {
    const store = useStore();

    const viewInExternalPortal = async (mfhId: number, assetPartnerId: number) => {
        try {
            const { partnerPreviewAccountUid: previewUid } = await grantMfhPortalView(mfhId, assetPartnerId);

            localStorage.setItem('__COCKPIT_RETURN_ROUTE__', window.location.pathname + window.location.search);
            localStorage.setItem('__PARTNER_VIEW_REDIRECT__', `/mfhs/${mfhId}`);

            store.commit('generalData/setImpersonatedUserId', previewUid);

            location.reload();

            return true;
        } catch (err: unknown) {
            const message = isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;
            showMessage(message ?? 'Could not open portal view', MessageType.Danger);
            return false;
        }
    };

    return { viewInExternalPortal };
};
